import React, { useState, useEffect, useCallback } from 'react';
import { Search, BookOpen, ChevronRight, ChevronDown, Plus, Pencil, Trash2, X, Save, ArrowLeft, Server, Database, Key, Zap, MapPin, Shield, Brain, Flame, GitCommit, Cloud, Box, MessageCircle, FileText, FolderTree, Lock, Users as UsersIcon, Radar, Sparkles, Check, AlertTriangle, GitBranch, Layers } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const CATEGORY_ICONS = {
  'Architecture': Server,
  'Schema': Database,
  'API Endpoints': Key,
  'Features': Zap,
  'Integrations': Cloud,
  'Changelog': GitCommit,
};

const CATEGORY_COLORS = {
  'Architecture': 'text-blue-400',
  'Schema': 'text-emerald-400',
  'API Endpoints': 'text-amber-400',
  'Features': 'text-violet-400',
  'Integrations': 'text-cyan-400',
  'Changelog': 'text-orange-400',
};

function MarkdownRenderer({ content }) {
  // Lightweight markdown renderer — no external dependency
  const renderMarkdown = (md) => {
    if (!md) return null;
    const lines = md.split('\n');
    const elements = [];
    let i = 0;
    let inCodeBlock = false;
    let codeLines = [];
    let listItems = [];
    let listType = null; // 'ul' or 'ol'
    let inTable = false;
    let tableRows = [];

    const flushList = () => {
      if (listItems.length > 0) {
        if (listType === 'ol') {
          elements.push(<ol key={`ol-${elements.length}`} className="list-decimal list-inside text-xs text-muted-foreground/70 mb-2 space-y-0.5 ml-1">{listItems.map((li, j) => <li key={j} className="leading-relaxed">{inlineFormat(li)}</li>)}</ol>);
        } else {
          elements.push(<ul key={`ul-${elements.length}`} className="list-disc list-inside text-xs text-muted-foreground/70 mb-2 space-y-0.5 ml-1">{listItems.map((li, j) => <li key={j} className="leading-relaxed">{inlineFormat(li)}</li>)}</ul>);
        }
        listItems = [];
        listType = null;
      }
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        const headerRow = tableRows[0];
        const bodyRows = tableRows.slice(2); // skip separator
        elements.push(
          <div key={`tbl-${elements.length}`} className="overflow-x-auto mb-3 mt-1">
            <table className="w-full text-[10px] border border-white/5 rounded-lg overflow-hidden">
              <thead className="bg-white/[0.04]">
                <tr>{headerRow.map((cell, ci) => <th key={ci} className="text-left px-2.5 py-1.5 font-bold text-muted-foreground/60 uppercase tracking-wider border-b border-white/5">{inlineFormat(cell.trim())}</th>)}</tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-white/[0.02] transition-colors">
                    {row.map((cell, ci) => <td key={ci} className="px-2.5 py-1.5 text-muted-foreground/70 border-b border-white/[0.03]">{inlineFormat(cell.trim())}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }
    };

    const inlineFormat = (text) => {
      if (!text) return text;
      // Process inline markdown: bold, italic, code, links
      const parts = [];
      let remaining = text;
      let key = 0;
      const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[(.+?)\]\((.+?)\))/g;
      let lastIndex = 0;
      let match;
      while ((match = regex.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
          parts.push(<span key={key++}>{remaining.slice(lastIndex, match.index)}</span>);
        }
        if (match[1]) { // bold
          parts.push(<strong key={key++} className="text-foreground/90 font-semibold">{match[2]}</strong>);
        } else if (match[3]) { // italic
          parts.push(<em key={key++}>{match[4]}</em>);
        } else if (match[5]) { // inline code
          parts.push(<code key={key++} className="bg-white/[0.06] text-primary/90 px-1.5 py-0.5 rounded text-[10px] font-mono">{match[6]}</code>);
        } else if (match[7]) { // link
          parts.push(<a key={key++} href={match[9]} className="text-primary hover:text-primary/80 underline underline-offset-2" target="_blank" rel="noopener noreferrer">{match[8]}</a>);
        }
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < remaining.length) {
        parts.push(<span key={key++}>{remaining.slice(lastIndex)}</span>);
      }
      return parts.length > 0 ? parts : text;
    };

    while (i < lines.length) {
      const line = lines[i];

      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${elements.length}`} className="bg-white/[0.03] border border-white/5 rounded-lg p-3 overflow-x-auto mb-3 mt-1">
              <code className="text-[10px] font-mono text-muted-foreground/80 leading-relaxed">{codeLines.join('\n')}</code>
            </pre>
          );
          codeLines = [];
          inCodeBlock = false;
        } else {
          flushList();
          flushTable();
          inCodeBlock = true;
        }
        i++;
        continue;
      }
      if (inCodeBlock) { codeLines.push(line); i++; continue; }

      // Tables
      if (line.includes('|') && line.trim().startsWith('|')) {
        flushList();
        const cells = line.split('|').slice(1, -1);
        if (cells.length > 0) {
          if (!inTable) inTable = true;
          tableRows.push(cells);
          i++;
          continue;
        }
      } else if (inTable) {
        flushTable();
      }

      // Headings
      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={`h3-${i}`} className="text-xs font-bold text-foreground/80 mb-1.5 mt-3">{inlineFormat(line.slice(4))}</h3>);
      } else if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={`h2-${i}`} className="text-sm font-heading font-bold text-foreground/90 mb-2 mt-4 border-b border-white/5 pb-1.5">{inlineFormat(line.slice(3))}</h2>);
      } else if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={`h1-${i}`} className="text-lg font-heading font-bold text-foreground mb-3 mt-1">{inlineFormat(line.slice(2))}</h1>);
      } else if (line.startsWith('---') || line.startsWith('***')) {
        flushList();
        elements.push(<hr key={`hr-${i}`} className="border-white/5 my-4" />);
      } else if (line.startsWith('> ')) {
        flushList();
        elements.push(<blockquote key={`bq-${i}`} className="border-l-2 border-primary/30 pl-3 text-muted-foreground/50 italic my-2 text-xs">{inlineFormat(line.slice(2))}</blockquote>);
      } else if (/^[\d]+\.\s/.test(line)) {
        if (listType !== 'ol') { flushList(); listType = 'ol'; }
        listItems.push(line.replace(/^[\d]+\.\s/, ''));
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        if (listType !== 'ul') { flushList(); listType = 'ul'; }
        listItems.push(line.slice(2));
      } else if (line.startsWith('  - ') || line.startsWith('  * ')) {
        // Nested list item — append to current
        listItems.push(line.slice(4));
      } else if (line.trim() === '') {
        flushList();
        flushTable();
      } else {
        flushList();
        elements.push(<p key={`p-${i}`} className="text-xs text-muted-foreground/70 leading-relaxed mb-2">{inlineFormat(line)}</p>);
      }
      i++;
    }
    flushList();
    flushTable();
    return elements;
  };

  return (
    <div className="prose-wiki" data-testid="doc-content-rendered">
      {renderMarkdown(content)}
    </div>
  );
}

function DocEditor({ doc, onSave, onCancel, categories }) {
  const [title, setTitle] = useState(doc?.title || '');
  const [category, setCategory] = useState(doc?.category || categories[0] || 'Architecture');
  const [content, setContent] = useState(doc?.content || '');
  const [tags, setTags] = useState(doc?.tags?.join(', ') || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        category,
        content,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-3"
      data-testid="doc-editor"
    >
      <div className="flex items-center gap-2">
        <button onClick={onCancel} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors" data-testid="doc-editor-cancel">
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-xs font-bold text-foreground/80">{doc ? 'Edit Document' : 'New Document'}</span>
      </div>

      <input
        data-testid="doc-editor-title"
        type="text"
        placeholder="Document title..."
        value={title}
        onChange={e => setTitle(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/40 transition-colors"
      />

      <div className="flex gap-2">
        <select
          data-testid="doc-editor-category"
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-primary/40 transition-colors flex-1"
        >
          {['Architecture', 'Schema', 'API Endpoints', 'Features', 'Integrations', 'Changelog'].map(c => (
            <option key={c} value={c} className="bg-bg">{c}</option>
          ))}
        </select>
        <input
          data-testid="doc-editor-tags"
          type="text"
          placeholder="Tags (comma-separated)"
          value={tags}
          onChange={e => setTags(e.target.value)}
          className="bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-primary/40 transition-colors flex-1"
        />
      </div>

      <textarea
        data-testid="doc-editor-content"
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder="Markdown content..."
        rows={18}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5 text-xs font-mono outline-none focus:border-primary/40 transition-colors resize-none leading-relaxed"
      />

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 bg-white/5 border border-white/10 hover:border-white/20 text-sm py-2.5 rounded-xl transition-colors font-medium"
          data-testid="doc-editor-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !title.trim() || !content.trim()}
          className="flex-1 bg-primary text-primary-foreground hover:opacity-90 font-bold text-sm py-2.5 rounded-xl transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="doc-editor-save-btn"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </motion.div>
  );
}

export default function AdminDocs() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Auto-documentation state
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showScanPanel, setShowScanPanel] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotSaved, setSnapshotSaved] = useState(false);

  const fetchDocs = useCallback(async (s = '', c = '') => {
    try {
      const params = new URLSearchParams();
      if (s) params.set('search', s);
      if (c) params.set('category', c);
      const res = await authFetch(`${API}/api/admin/docs?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDocs(data.docs || []);
        setCategories(data.categories || []);
        // Auto-expand all categories
        const exp = {};
        (data.categories || []).forEach(cat => { exp[cat] = true; });
        setExpandedCats(prev => ({ ...exp, ...prev }));
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleSearch = (val) => {
    setSearch(val);
    fetchDocs(val, activeCategory);
    setSelectedDoc(null);
  };

  const handleCategoryFilter = (cat) => {
    const next = activeCategory === cat ? '' : cat;
    setActiveCategory(next);
    fetchDocs(search, next);
    setSelectedDoc(null);
  };

  const toggleCat = (cat) => {
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleSaveDoc = async (payload) => {
    if (editingDoc) {
      const res = await authFetch(`${API}/api/admin/docs/${editingDoc.doc_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        setEditingDoc(null);
        setSelectedDoc(updated);
        fetchDocs(search, activeCategory);
      }
    } else {
      const res = await authFetch(`${API}/api/admin/docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
      });
      if (res.ok) {
        const created = await res.json();
        setCreating(false);
        setSelectedDoc(created);
        fetchDocs(search, activeCategory);
      }
    }
  };

  const handleDelete = async (docId) => {
    await authFetch(`${API}/api/admin/docs/${docId}`, { method: 'DELETE',  });
    setDeleteConfirm(null);
    setSelectedDoc(null);
    fetchDocs(search, activeCategory);
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    setDraft(null);
    setSnapshotSaved(false);
    try {
      const res = await authFetch(`${API}/api/admin/docs/scan`, { method: 'POST',  });
      if (res.ok) {
        const data = await res.json();
        setScanResult(data);
        setShowScanPanel(true);
      }
    } catch {} finally { setScanning(false); }
  };

  const handleGenerateChangelog = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/api/admin/docs/generate-changelog`, {
        method: 'POST',       });
      if (res.ok) {
        const data = await res.json();
        if (data.has_draft) setDraft(data.draft);
        else setDraft({ title: 'No changes', content: data.message, tags: [] });
      }
    } catch {} finally { setGenerating(false); }
  };

  const handleSaveDraft = async () => {
    if (!draft) return;
    const res = await authFetch(`${API}/api/admin/docs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
        title: draft.title,
        category: 'Changelog',
        content: draft.content,
        tags: draft.tags || ['changelog', 'auto-generated'],
        sort_order: 0,
      }),
    });
    if (res.ok) {
      const created = await res.json();
      setDraft(null);
      setShowScanPanel(false);
      setSelectedDoc(created);
      fetchDocs(search, activeCategory);
    }
  };

  const handleSaveSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const res = await authFetch(`${API}/api/admin/docs/snapshot`, { method: 'POST',  });
      if (res.ok) { setSnapshotSaved(true); }
    } catch {} finally { setSavingSnapshot(false); }
  };

  const handleEditDraft = () => {
    if (!draft) return;
    setEditingDoc(null);
    setCreating(false);
    // Open editor pre-filled with draft
    setEditingDoc({
      title: draft.title,
      category: 'Changelog',
      content: draft.content,
      tags: draft.tags || ['changelog', 'auto-generated'],
    });
    setShowScanPanel(false);
  };

  // Group docs by category
  const grouped = {};
  docs.forEach(d => {
    if (!grouped[d.category]) grouped[d.category] = [];
    grouped[d.category].push(d);
  });
  const orderedCats = ['Architecture', 'Schema', 'API Endpoints', 'Features', 'Integrations', 'Changelog'].filter(c => grouped[c]);

  if (creating || editingDoc) {
    return (
      <DocEditor
        doc={editingDoc}
        categories={categories}
        onSave={handleSaveDoc}
        onCancel={() => { setCreating(false); setEditingDoc(null); }}
      />
    );
  }

  if (showScanPanel) {
    const rd = scanResult?.route_diff;
    const cd = scanResult?.collection_diff;
    const hasChanges = scanResult?.has_changes;
    const totalRouteChanges = (rd?.added?.length || 0) + (rd?.removed?.length || 0);
    const totalSchemaChanges = (cd?.added_collections?.length || 0) + (cd?.removed_collections?.length || 0) + (cd?.field_changes?.length || 0);

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3" data-testid="scan-panel">
        <div className="flex items-center justify-between">
          <button onClick={() => setShowScanPanel(false)} className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors" data-testid="scan-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Docs
          </button>
          <div className="flex items-center gap-1.5">
            <Radar className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-[10px] uppercase tracking-widest text-violet-400/70 font-medium">Auto-Doc Scanner</span>
          </div>
        </div>

        {/* Scan Summary */}
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-foreground/80">Scan Results</p>
            {scanResult?.is_first_scan && (
              <span className="text-[8px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-medium">First Scan</span>
            )}
          </div>

          {!hasChanges ? (
            <div className="text-center py-4">
              <Check className="w-5 h-5 text-green-400/60 mx-auto mb-1.5" />
              <p className="text-xs text-green-400/80 font-medium">Everything is up to date</p>
              <p className="text-[9px] text-muted-foreground/30 mt-0.5">No changes detected since last snapshot</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Route Changes */}
              {totalRouteChanges > 0 && (
                <div className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <GitBranch className="w-3 h-3 text-amber-400/60" />
                    <span className="text-[9px] font-bold text-amber-400/80 uppercase tracking-wider">API Routes ({totalRouteChanges} change{totalRouteChanges !== 1 ? 's' : ''})</span>
                  </div>
                  {rd?.added?.length > 0 && (
                    <div className="mb-1.5">
                      <p className="text-[8px] text-green-400/60 font-medium mb-0.5">+ Added</p>
                      {rd.added.map((r, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground/60 font-mono ml-2">
                          <span className="text-green-400/50">{r.methods.join(', ')}</span> {r.path}
                        </p>
                      ))}
                    </div>
                  )}
                  {rd?.removed?.length > 0 && (
                    <div>
                      <p className="text-[8px] text-red-400/60 font-medium mb-0.5">- Removed</p>
                      {rd.removed.map((r, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground/60 font-mono ml-2 line-through opacity-60">
                          <span className="text-red-400/50">{r.methods.join(', ')}</span> {r.path}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Schema Changes */}
              {totalSchemaChanges > 0 && (
                <div className="bg-white/[0.02] border border-white/[0.04] rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Layers className="w-3 h-3 text-emerald-400/60" />
                    <span className="text-[9px] font-bold text-emerald-400/80 uppercase tracking-wider">Schema ({totalSchemaChanges} change{totalSchemaChanges !== 1 ? 's' : ''})</span>
                  </div>
                  {cd?.added_collections?.length > 0 && (
                    <div className="mb-1.5">
                      <p className="text-[8px] text-green-400/60 font-medium mb-0.5">+ New Collections</p>
                      {cd.added_collections.map((c, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground/60 font-mono ml-2">
                          <span className="text-green-400/50">{c.name}</span> <span className="text-muted-foreground/30">({c.document_count} docs, {c.fields.length} fields)</span>
                        </p>
                      ))}
                    </div>
                  )}
                  {cd?.field_changes?.length > 0 && (
                    <div>
                      <p className="text-[8px] text-amber-400/60 font-medium mb-0.5">~ Field Changes</p>
                      {cd.field_changes.map((ch, i) => (
                        <div key={i} className="ml-2 mb-1">
                          <p className="text-[10px] font-mono text-muted-foreground/60">{ch.collection}:</p>
                          {ch.added_fields.length > 0 && <p className="text-[9px] text-green-400/50 ml-2">+ {ch.added_fields.join(', ')}</p>}
                          {ch.removed_fields.length > 0 && <p className="text-[9px] text-red-400/50 ml-2">- {ch.removed_fields.join(', ')}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Totals */}
              <div className="flex items-center gap-3 text-[9px] text-muted-foreground/30 pt-1">
                <span>{scanResult?.current_snapshot?.route_count} total routes</span>
                <span>{scanResult?.current_snapshot?.collection_count} collections</span>
                {scanResult?.last_snapshot_at && (
                  <span>last scan: {new Date(scanResult.last_snapshot_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* AI Changelog Draft */}
        {draft && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="bg-violet-500/[0.04] border border-violet-500/15 rounded-2xl p-4 space-y-3" data-testid="changelog-draft">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-violet-400" />
              <span className="text-[10px] font-bold text-violet-400/80">AI-Generated Changelog Draft</span>
            </div>
            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 max-h-[300px] overflow-y-auto">
              <MarkdownRenderer content={draft.content} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDraft(null)} className="flex-1 bg-white/5 border border-white/10 hover:border-white/20 text-[10px] py-2 rounded-xl transition-colors font-medium" data-testid="draft-discard-btn">
                Discard
              </button>
              <button onClick={handleEditDraft} className="flex-1 bg-white/5 border border-violet-500/20 hover:border-violet-500/40 text-[10px] text-violet-400 py-2 rounded-xl transition-colors font-medium" data-testid="draft-edit-btn">
                <Pencil className="w-3 h-3 inline mr-1" /> Edit
              </button>
              <button onClick={handleSaveDraft} className="flex-1 bg-violet-500/20 border border-violet-500/30 hover:bg-violet-500/30 text-[10px] text-violet-300 font-bold py-2 rounded-xl transition-colors" data-testid="draft-save-btn">
                <Save className="w-3 h-3 inline mr-1" /> Save to Wiki
              </button>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {hasChanges && !draft && (
            <button
              onClick={handleGenerateChangelog}
              disabled={generating}
              className="flex-1 bg-violet-500/15 border border-violet-500/25 hover:border-violet-500/40 text-violet-400 font-bold text-[10px] py-2.5 rounded-xl transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
              data-testid="generate-changelog-btn"
            >
              <Sparkles className="w-3 h-3" />
              {generating ? 'Generating...' : 'Generate Changelog with AI'}
            </button>
          )}
          {hasChanges && (
            <button
              onClick={handleSaveSnapshot}
              disabled={savingSnapshot || snapshotSaved}
              className={`${draft ? 'flex-1' : 'flex-none px-4'} bg-white/5 border border-white/10 hover:border-white/20 text-[10px] py-2.5 rounded-xl transition-colors font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-40 ${snapshotSaved ? 'text-green-400 border-green-400/20' : ''}`}
              data-testid="save-snapshot-btn"
            >
              {snapshotSaved ? <><Check className="w-3 h-3" /> Baseline Saved</> : savingSnapshot ? 'Saving...' : 'Save as Baseline'}
            </button>
          )}
        </div>
      </motion.div>
    );
  }

  if (selectedDoc) {
    const CatIcon = CATEGORY_ICONS[selectedDoc.category] || FileText;
    const catColor = CATEGORY_COLORS[selectedDoc.category] || 'text-muted-foreground';
    return (
      <motion.div
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        className="space-y-3"
        data-testid="doc-detail-view"
      >
        {/* Back + actions bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedDoc(null)}
            className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="doc-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Docs
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setEditingDoc(selectedDoc)}
              className="p-1.5 hover:bg-white/5 rounded-lg transition-colors"
              data-testid="doc-edit-btn"
            >
              <Pencil className="w-3 h-3 text-muted-foreground/50 hover:text-primary" />
            </button>
            {deleteConfirm === selectedDoc.doc_id ? (
              <div className="flex items-center gap-1">
                <button onClick={() => handleDelete(selectedDoc.doc_id)} className="text-[9px] bg-red-500/20 text-red-400 px-2 py-1 rounded-lg font-medium" data-testid="doc-delete-confirm-btn">Delete</button>
                <button onClick={() => setDeleteConfirm(null)} className="text-[9px] bg-white/5 px-2 py-1 rounded-lg" data-testid="doc-delete-cancel-btn">Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setDeleteConfirm(selectedDoc.doc_id)}
                className="p-1.5 hover:bg-white/5 rounded-lg transition-colors"
                data-testid="doc-delete-btn"
              >
                <Trash2 className="w-3 h-3 text-muted-foreground/50 hover:text-red-400" />
              </button>
            )}
          </div>
        </div>

        {/* Category badge */}
        <div className="flex items-center gap-2">
          <div className={`inline-flex items-center gap-1 text-[9px] font-medium px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/5 ${catColor}`}>
            <CatIcon className="w-2.5 h-2.5" />
            {selectedDoc.category}
          </div>
          {selectedDoc.version > 1 && (
            <span className="text-[8px] text-muted-foreground/30">v{selectedDoc.version}</span>
          )}
        </div>

        {/* Tags */}
        {selectedDoc.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedDoc.tags.map(tag => (
              <span key={tag} className="text-[8px] bg-white/[0.04] text-muted-foreground/40 px-1.5 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="bg-white/[0.015] border border-white/5 rounded-2xl p-4 min-h-[200px]">
          <MarkdownRenderer content={selectedDoc.content} />
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-[8px] text-muted-foreground/25 px-1">
          <span>Updated {selectedDoc.updated_at ? new Date(selectedDoc.updated_at).toLocaleDateString() : '—'}</span>
          <span>by {selectedDoc.updated_by || 'system'}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-docs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-medium">Deep Wiki</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleScan}
            disabled={scanning}
            className="inline-flex items-center gap-1 text-[9px] bg-violet-500/10 text-violet-400 border border-violet-500/20 hover:border-violet-500/40 px-2.5 py-1.5 rounded-full font-medium transition-colors disabled:opacity-40"
            data-testid="doc-scan-btn"
          >
            <Radar className={`w-3 h-3 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning...' : 'Auto-Scan'}
          </button>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 text-[9px] bg-primary/10 text-primary border border-primary/20 hover:border-primary/40 px-2.5 py-1.5 rounded-full font-medium transition-colors"
            data-testid="doc-create-btn"
          >
            <Plus className="w-3 h-3" />
            New Doc
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/30" />
        <input
          data-testid="docs-search-input"
          type="text"
          placeholder="Search docs, schemas, endpoints..."
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/5 focus:border-primary/30 rounded-xl pl-9 pr-3 py-2.5 text-xs outline-none transition-colors placeholder:text-muted-foreground/25"
        />
        {search && (
          <button onClick={() => handleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-3 h-3 text-muted-foreground/30 hover:text-foreground transition-colors" />
          </button>
        )}
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-1 pb-0.5">
        {['Architecture', 'Schema', 'API Endpoints', 'Features', 'Integrations', 'Changelog'].map(cat => {
          const CatIcon = CATEGORY_ICONS[cat] || FileText;
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => handleCategoryFilter(cat)}
              data-testid={`docs-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-[9px] px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 transition-all font-medium ${
                isActive
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'bg-white/[0.03] text-muted-foreground/50 border border-white/[0.03] hover:border-white/10'
              }`}
            >
              <CatIcon className="w-2.5 h-2.5" />
              {cat}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="flex justify-center space-x-1.5">
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
            <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
          </div>
        </div>
      )}

      {/* Doc Tree */}
      {!loading && (
        <div className="space-y-1" data-testid="docs-tree">
          {orderedCats.length === 0 && (
            <div className="text-center py-8">
              <BookOpen className="w-6 h-6 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground/30">
                {search ? 'No docs match your search' : 'No documentation yet'}
              </p>
            </div>
          )}

          {orderedCats.map(cat => {
            const CatIcon = CATEGORY_ICONS[cat] || FileText;
            const catColor = CATEGORY_COLORS[cat] || 'text-muted-foreground';
            const isExpanded = expandedCats[cat] !== false;
            const catDocs = grouped[cat] || [];

            return (
              <div key={cat}>
                <button
                  onClick={() => toggleCat(cat)}
                  className="w-full flex items-center gap-2 py-2 px-2 hover:bg-white/[0.02] rounded-lg transition-colors group"
                  data-testid={`docs-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3 h-3 text-muted-foreground/30" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-muted-foreground/30" />
                  )}
                  <CatIcon className={`w-3 h-3 ${catColor} opacity-60`} />
                  <span className={`text-[10px] font-bold tracking-wide ${catColor} opacity-80`}>{cat}</span>
                  <span className="text-[8px] text-muted-foreground/20 ml-auto">{catDocs.length}</span>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-5 border-l border-white/[0.04] pl-2.5 space-y-0.5 pb-1">
                        {catDocs.map(doc => (
                          <button
                            key={doc.doc_id}
                            onClick={() => setSelectedDoc(doc)}
                            className="w-full text-left flex items-center gap-2 py-1.5 px-2 hover:bg-white/[0.03] rounded-lg transition-colors group"
                            data-testid={`doc-item-${doc.doc_id}`}
                          >
                            <FileText className="w-2.5 h-2.5 text-muted-foreground/20 group-hover:text-muted-foreground/40 transition-colors flex-shrink-0" />
                            <span className="text-[11px] text-muted-foreground/60 group-hover:text-foreground/80 transition-colors truncate">
                              {doc.title}
                            </span>
                            {doc.version > 1 && (
                              <span className="text-[7px] text-muted-foreground/20 ml-auto flex-shrink-0">v{doc.version}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats footer */}
      {!loading && docs.length > 0 && (
        <div className="text-center pt-2 border-t border-white/[0.03]">
          <p className="text-[8px] text-muted-foreground/20">
            {docs.length} doc{docs.length !== 1 ? 's' : ''} across {orderedCats.length} categor{orderedCats.length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
      )}
    </div>
  );
}
